import { Link, type LinkProps } from '@ironhack/design-system2/components/link';
import { Pagination } from '@ironhack/design-system2/components/pagination';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { type PropsWithChildren, type ReactElement } from 'react';

import { getBaseUrlFromPaginationUrl } from '@/lib/utils';

const getPageUrl =
  (nextPathname: string) =>
  (pageNumber: number): string => {
    const { pathname, search } =
      typeof window === 'undefined'
        ? { pathname: nextPathname, search: '' }
        : window.location;
    const pagePathname = `${getBaseUrlFromPaginationUrl(pathname)}${
      pageNumber > 1 ? `/page/${pageNumber}` : ''
    }`;
    return pagePathname + search;
  };

const LinkComponent = ({
  ariaLabel,
  children,
  href,
  rel,
  style,
}: PropsWithChildren<
  {
    ariaLabel?: string;
  } & Pick<LinkProps, 'style' | 'rel' | 'href'>
>) => (
  <Link
    aria-label={ariaLabel}
    as={NextLink}
    href={href}
    rel={rel}
    style={style}
  >
    {children}
  </Link>
);

type Props = {
  articleCount: number;
  articlesPerPage: number;
  paginationParams: string[] | undefined;
};

export const ArticlePagination = (props: Props): ReactElement => {
  const { articleCount, articlesPerPage, paginationParams } = props;

  const pagesCount = Math.ceil(articleCount / articlesPerPage);
  const page = paginationParams?.[paginationParams?.indexOf('page') + 1] ?? '1';
  const activePage = Number.parseInt(page);

  const pathname = usePathname() ?? '';

  return (
    <Pagination
      LinkComponent={LinkComponent}
      activePage={activePage}
      getPageUrl={getPageUrl(pathname)}
      justifyContent="center"
      pagesCount={pagesCount}
      w="full"
    />
  );
};
